<template>
  <div class="signature-pad">
    <canvas
      ref="canvas"
      class="signature-pad__canvas"
      :style="{ height: height + 'px' }"
      @pointerdown="start"
      @pointermove="move"
      @pointerup="end"
      @pointerleave="end"
      @pointercancel="end"
    ></canvas>
    <!-- The full-screen dialog puts Tühjenda in its own action bar, so the pad
         hides its own when it is being driven from there. -->
    <div class="signature-pad__bar" v-if="!hideBar">
      <span class="text-caption text-grey">{{ label }}</span>
      <v-spacer />
      <v-btn size="small" variant="text" @click="clear">Tühjenda</v-btn>
    </div>
  </div>
</template>

<script>
// Touch/pen/mouse signature capture on an HTML5 canvas (no external deps).
// Parent reads the result with this.$refs.pad.toDataURL() / .isEmpty(), and can
// follow the empty/not-empty state through the `change` event.

// What the server keeps (sharp resizes to this) and far more than the ~50pt box
// the PDF prints a signature in. A full-screen pad on a desktop is several
// thousand pixels wide, and its data URL travels in a JSON body with a 100kb
// limit — so the capture is scaled down to this rather than the encoded size
// being left to the size of the user's monitor.
const MAX_WIDTH = 800

export default {
  props: {
    label: { type: String, default: 'Allkiri' },
    height: { type: Number, default: 160 },
    hideBar: { type: Boolean, default: false }
  },
  data () {
    return {
      drawing: false,
      empty: true,
      last: null
    }
  },
  mounted () {
    this.resize()
    window.addEventListener('resize', this.resize)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.resize)
  },
  methods: {
    ctx () {
      return this.$refs.canvas.getContext('2d')
    },
    // Match the canvas backing store to its CSS size (preserve the drawing).
    resize () {
      const canvas = this.$refs.canvas
      const ratio = window.devicePixelRatio || 1
      const w = Math.round(canvas.clientWidth * ratio)
      const h = Math.round(canvas.clientHeight * ratio)
      // Bail out when the pixel size is unchanged. Resetting canvas.width/height
      // clears the backing store, and the redraw below is asynchronous — a resize
      // fired between drawing and capture (e.g. the soft keyboard closing when the
      // user taps "save") would otherwise blank the signature. Keyboard show/hide
      // doesn't change the canvas width, so most resizes are safely no-ops here.
      if (w === canvas.width && h === canvas.height) return
      const data = this.empty ? null : canvas.toDataURL()
      // The size the drawing was made at, in the same units it is redrawn in.
      const oldW = canvas.width / ratio
      const oldH = canvas.height / ratio
      canvas.width = w
      canvas.height = h
      const ctx = this.ctx()
      ctx.scale(ratio, ratio)
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = '#0d2c54'
      if (data) {
        const img = new Image()
        // Scaled to fit, keeping its proportions: stretched to the new width
        // and height, a signature begun before the phone was turned came out
        // squashed.
        img.onload = () => {
          const scale = oldW && oldH ? Math.min(canvas.clientWidth / oldW, canvas.clientHeight / oldH) : 1
          ctx.drawImage(img, 0, 0, oldW * scale || canvas.clientWidth, oldH * scale || canvas.clientHeight)
        }
        img.src = data
      }
    },
    pos (e) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    },
    // One pointer draws: the one that began the stroke. A palm resting on the
    // glass, or a second finger, used to be taken for the pen as well, and the
    // line jumped between the two.
    start (e) {
      if (this.drawing) return
      this.$refs.canvas.setPointerCapture && this.$refs.canvas.setPointerCapture(e.pointerId)
      this.drawing = true
      this.pointer = e.pointerId
      this.last = this.pos(e)
      // A tap is a dot — the ones on i, j, ä, ö and ü. Without it a touch that
      // did not move left nothing.
      const ctx = this.ctx()
      ctx.beginPath()
      ctx.arc(this.last.x, this.last.y, ctx.lineWidth / 2, 0, Math.PI * 2)
      ctx.fillStyle = ctx.strokeStyle
      ctx.fill()
      this.setEmpty(false)
    },
    move (e) {
      if (!this.drawing || e.pointerId !== this.pointer) return
      e.preventDefault()
      const p = this.pos(e)
      const ctx = this.ctx()
      ctx.beginPath()
      ctx.moveTo(this.last.x, this.last.y)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
      this.last = p
      this.setEmpty(false)
    },
    end (e) {
      if (e && e.pointerId !== undefined && e.pointerId !== this.pointer) return
      this.drawing = false
      this.pointer = null
    },
    clear () {
      const canvas = this.$refs.canvas
      this.ctx().clearRect(0, 0, canvas.width, canvas.height)
      this.setEmpty(true)
    },
    setEmpty (empty) {
      if (this.empty === empty) return
      this.empty = empty
      this.$emit('change', !empty)
    },
    // Size the pad to the space it now occupies and start from `dataUrl` (or from
    // blank when there is none). Mounted inside a dialog the canvas has no width
    // until the dialog is on screen, so the parent calls this once it is —
    // resize() alone deliberately does nothing while the pixel size is unchanged.
    reset (dataUrl, attempt = 0) {
      const canvas = this.$refs.canvas
      if (!canvas) return
      // A dialog that is still animating in can report zero width for a frame or
      // two. Sizing the canvas to that would leave the pad permanently unusable,
      // so wait for a real width rather than trusting the first measurement.
      if (!canvas.clientWidth && attempt < 10) {
        window.requestAnimationFrame(() => this.reset(dataUrl, attempt + 1))
        return
      }
      // Before resize(), so its preserve-the-drawing path doesn't carry the last
      // signature over into a pad that is meant to start from `dataUrl`.
      this.setEmpty(true)
      this.resize()
      this.ctx().clearRect(0, 0, canvas.width, canvas.height)
      if (!dataUrl) return
      const img = new Image()
      img.onload = () => {
        // The stored signature is up to MAX_WIDTH wide; fit it into the pad
        // without stretching, anchored top-left where it was drawn.
        const w = canvas.clientWidth
        const h = canvas.clientHeight
        const scale = Math.min(w / img.width, h / img.height, 1)
        this.ctx().drawImage(img, 0, 0, img.width * scale, img.height * scale)
        this.setEmpty(false)
      }
      img.src = dataUrl
    },
    isEmpty () {
      return this.empty
    },
    toDataURL () {
      const canvas = this.$refs.canvas
      if (!canvas.width || canvas.width <= MAX_WIDTH) {
        return canvas.toDataURL('image/png')
      }
      const scale = MAX_WIDTH / canvas.width
      const out = document.createElement('canvas')
      out.width = MAX_WIDTH
      out.height = Math.max(1, Math.round(canvas.height * scale))
      out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height)
      return out.toDataURL('image/png')
    }
  }
}
</script>

<style scoped>
.signature-pad__canvas {
  width: 100%;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  touch-action: none;
  display: block;
}
.signature-pad__bar {
  display: flex;
  align-items: center;
  margin-top: 2px;
}
</style>
